import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatBRL, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { toast } from "sonner";
import { Heart, ShoppingBag, MessageCircle, MapPin, Minus, Plus, Truck, PackageX } from "lucide-react";
import ProductCard from "@/components/ProductCard";

export default function Product() {
  const { slug } = useParams();
  const [product, setProduct] = useState(null);
  const [selectedImage, setSelectedImage] = useState(0);
  const [size, setSize] = useState(null);
  const [color, setColor] = useState(null);
  const [qty, setQty] = useState(1);
  const [related, setRelated] = useState([]);
  const [isFav, setIsFav] = useState(false);
  const [favLoading, setFavLoading] = useState(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    setProduct(null); setSize(null); setColor(null); setSelectedImage(0); setIsFav(false); setQty(1);
    api.get(`/products/${slug}`).then((r) => {
      setProduct(r.data);
      api.get(`/products?category=${encodeURIComponent(r.data.category)}&limit=8`).then((rr) => {
        setRelated(rr.data.filter((p) => p.id !== r.data.id).slice(0, 4));
      });
      if (user) {
        api.get("/favorites").then((fr) => setIsFav(fr.data.includes(r.data.id))).catch(() => {});
      }
    });
  }, [slug, user]);

  const toggleFavorite = async () => {
    if (!user) {
      toast.info("Entre na sua conta para favoritar produtos");
      nav(`/entrar?next=/produto/${slug}`);
      return;
    }
    if (!product || favLoading) return;
    setFavLoading(true);
    try {
      if (isFav) {
        await api.delete(`/favorites/${product.id}`);
        setIsFav(false);
        toast.success("Removido dos favoritos");
      } else {
        await api.post("/favorites", { product_id: product.id });
        setIsFav(true);
        toast.success("Adicionado aos favoritos");
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setFavLoading(false);
    }
  };

  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center">Carregando...</div>;

  const price = product.sale_price ?? product.price;
  const hasSale = !!product.sale_price;
  const stockNum = Number(product.stock ?? 0);
  const inStock = stockNum > 0; // estoque total do produto — controla o bloco "indisponível"
  const hasVariants = !!product.has_variants && (product.variants || []).length > 0;

  // Estoque de uma combinação cor+tamanho específica (0 se a combinação não existir).
  const variantStockFor = (c, s) => {
    const v = (product.variants || []).find((it) => it.color === c && it.size === s);
    return v ? Number(v.stock || 0) : 0;
  };
  const colorStockTotal = (c) => (product.variants || []).filter((v) => v.color === c).reduce((sum, v) => sum + Number(v.stock || 0), 0);

  const effectiveStock = hasVariants ? (color && size ? variantStockFor(color, size) : 0) : stockNum;
  const canSelect = hasVariants ? (color && size && effectiveStock > 0) : true;
  const canAdd = inStock && canSelect;
  const maxQty = Math.max(1, effectiveStock);

  // Para produtos com variante, trocar de cor pode invalidar o tamanho/estoque
  // selecionados, então ambos são resetados. Produtos simples não têm essa
  // dependência entre cor e tamanho — mantém o comportamento original (independentes).
  const selectColor = (c) => {
    setColor(c);
    if (hasVariants) {
      setSize(null);
      setQty(1);
    }
  };
  const selectSize = (s) => {
    setSize(s);
    if (hasVariants) {
      setQty(1);
    }
  };

  const handleAdd = () => {
    if (!inStock) return;
    if (hasVariants) {
      if (!color) return toast.error("Selecione uma cor");
      if (!size) return toast.error("Selecione um tamanho");
      if (effectiveStock <= 0) return toast.error("Essa combinação está esgotada");
    } else {
      if (product.sizes?.length > 0 && !size) return toast.error("Selecione um tamanho");
      if (product.colors?.length > 0 && !color) return toast.error("Selecione uma cor");
    }
    const cartProduct = hasVariants ? { ...product, stock: effectiveStock } : product;
    addItem(cartProduct, { size, color, quantity: qty });
  };

  const handleBuyNow = () => {
    if (!inStock) return;
    if (hasVariants) {
      if (!color) return toast.error("Selecione uma cor");
      if (!size) return toast.error("Selecione um tamanho");
      if (effectiveStock <= 0) return toast.error("Essa combinação está esgotada");
    } else {
      if (product.sizes?.length > 0 && !size) return toast.error("Selecione um tamanho");
      if (product.colors?.length > 0 && !color) return toast.error("Selecione uma cor");
    }
    const cartProduct = hasVariants ? { ...product, stock: effectiveStock } : product;
    if (addItem(cartProduct, { size, color, quantity: qty })) {
      setTimeout(() => window.location.href = "/checkout", 200);
    }
  };

  const wpMsg = `Olá! Vi este produto no site da Nalin e gostaria de mais informações: ${product.name} - ${window.location.href}`;
  const wpUnavailableMsg = `Olá! O produto "${product.name}" está indisponível no site. Poderiam me avisar quando voltar ao estoque?`;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 sm:py-10">
      <nav className="text-xs text-neutral-500 mb-4">
        <Link to="/">Início</Link> / <Link to={`/categoria/${product.category}`}>{product.category}</Link> / <span className="text-neutral-900">{product.name}</span>
      </nav>

      <div className="grid lg:grid-cols-2 gap-8 lg:gap-14">
        <div className="space-y-3">
          <div className="aspect-[4/5] bg-neutral-100 rounded-xl overflow-hidden">
            <img src={product.images?.[selectedImage] || product.images?.[0]} alt={product.name} className="w-full h-full object-cover" />
          </div>
          {product.images?.length > 1 && (
            <div className="grid grid-cols-5 gap-2">
              {product.images.map((img, i) => (
                <button key={i} onClick={() => setSelectedImage(i)} className={`aspect-square rounded-md overflow-hidden border-2 ${selectedImage === i ? "border-[#C91F1F]" : "border-transparent"}`}>
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="space-y-5">
          <div>
            <p className="text-xs text-neutral-500 uppercase tracking-widest">{product.category}</p>
            <div className="flex items-start justify-between gap-3 mt-1">
              <h1 className="text-2xl sm:text-3xl font-bold flex-1" style={{ fontFamily: "Outfit" }}>{product.name}</h1>
              <button
                type="button"
                onClick={toggleFavorite}
                disabled={favLoading}
                data-testid="product-favorite-btn"
                aria-label={isFav ? "Remover dos favoritos" : "Adicionar aos favoritos"}
                aria-pressed={isFav}
                className={`shrink-0 h-11 w-11 rounded-full border flex items-center justify-center transition-all ${isFav ? "bg-[#C91F1F] border-[#C91F1F] text-white" : "border-neutral-300 text-neutral-700 hover:border-[#C91F1F] hover:text-[#C91F1F]"} disabled:opacity-60`}
              >
                <Heart className="h-5 w-5" fill={isFav ? "currentColor" : "none"} />
              </button>
            </div>
            <p className="text-xs text-neutral-400 mt-1">SKU: {product.sku}</p>
          </div>

          <div className="space-y-1">
            {hasSale && <span className="text-neutral-400 line-through">{formatBRL(product.price)}</span>}
            <div className="text-3xl font-extrabold text-[#C91F1F]">{formatBRL(price)}</div>
            <p className="text-sm text-neutral-600">ou 3x de {formatBRL(price / 3)} sem juros</p>
          </div>

          {product.colors?.length > 0 && (
            <div>
              <p className="text-sm font-medium mb-2">Cor: <span className="text-neutral-500 font-normal">{color || "Selecione"}</span></p>
              <div className="flex flex-wrap gap-2">
                {product.colors.map((c) => {
                  const disabled = hasVariants && colorStockTotal(c) === 0;
                  return (
                    <button
                      key={c}
                      onClick={() => !disabled && selectColor(c)}
                      disabled={disabled}
                      data-testid={`color-${c}`}
                      title={disabled ? "Esgotado" : undefined}
                      className={`border rounded-full px-4 py-1.5 text-sm ${
                        disabled
                          ? "border-neutral-200 text-neutral-400 line-through cursor-not-allowed"
                          : color === c ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"
                      }`}
                    >
                      {c}{disabled && <span className="ml-1 text-[10px] no-underline">(Esgotado)</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {product.sizes?.length > 0 && (
            <div>
              <div className="flex justify-between items-center mb-2">
                <p className="text-sm font-medium">Tamanho: <span className="text-neutral-500 font-normal">{size || "Selecione"}</span></p>
                <button className="text-xs underline text-neutral-600">Guia de medidas</button>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.sizes.map((s) => {
                  const disabled = hasVariants && (!color || variantStockFor(color, s) === 0);
                  return (
                    <button
                      key={s}
                      onClick={() => !disabled && selectSize(s)}
                      disabled={disabled}
                      data-testid={`size-${s}`}
                      title={hasVariants && color && disabled ? "Esgotado" : undefined}
                      className={`min-w-[48px] h-12 border rounded-md text-sm font-medium ${
                        disabled
                          ? "border-neutral-200 text-neutral-400 line-through cursor-not-allowed"
                          : size === s ? "bg-neutral-900 text-white border-neutral-900" : "border-neutral-300 hover:border-neutral-900"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              {hasVariants && color && (
                <p className="text-xs text-neutral-500 mt-1">Tamanhos esgotados para {color} aparecem desabilitados.</p>
              )}
            </div>
          )}

          <div className="flex items-center gap-3">
            <div className="flex items-center border rounded-md">
              <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 hover:bg-neutral-100" aria-label="Diminuir"><Minus className="h-4 w-4" /></button>
              <span className="px-4 font-medium min-w-[3ch] text-center" data-testid="qty-value">{qty}</span>
              <button onClick={() => setQty(Math.min(maxQty, qty + 1))} disabled={!canSelect || qty >= maxQty} className="p-3 hover:bg-neutral-100 disabled:opacity-50" aria-label="Aumentar"><Plus className="h-4 w-4" /></button>
            </div>
            <span className={`text-xs font-medium ${canSelect ? "text-green-700" : "text-red-600"}`} data-testid="stock-info">
              {hasVariants
                ? (color && size ? (effectiveStock > 0 ? `${effectiveStock} em estoque` : "Esgotado nessa combinação") : "Selecione cor e tamanho")
                : (inStock ? `${stockNum} em estoque` : "Indisponível")}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <Button data-testid="add-to-cart-btn" disabled={!canAdd} onClick={handleAdd} className="bg-[#C91F1F] hover:bg-[#9F1515] h-12 font-semibold">
              <ShoppingBag className="h-4 w-4 mr-2" /> Adicionar ao carrinho
            </Button>
            <Button data-testid="buy-now-btn" disabled={!canAdd} onClick={handleBuyNow} variant="outline" className="h-12 font-semibold border-neutral-900">
              Comprar agora
            </Button>
          </div>

          {inStock ? (
            <a href={`https://wa.me/5521984953716?text=${encodeURIComponent(wpMsg)}`} target="_blank" rel="noreferrer" data-testid="whatsapp-buy-link">
              <Button className="w-full bg-[#25D366] hover:bg-[#1ea952] h-11 font-semibold">
                <MessageCircle className="h-4 w-4 mr-2" /> Comprar pelo WhatsApp
              </Button>
            </a>
          ) : (
            <div className="w-full rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-4 text-center space-y-2" data-testid="out-of-stock-block">
              <div className="flex items-center justify-center gap-2 text-neutral-600 font-semibold"><PackageX className="h-4 w-4" /> Produto indisponível</div>
              <p className="text-xs text-neutral-500">Este item está esgotado no momento. Avise-nos e retornamos quando repuser em estoque.</p>
              <a href={`https://wa.me/5521984953716?text=${encodeURIComponent(wpUnavailableMsg)}`} target="_blank" rel="noreferrer" data-testid="whatsapp-notify-link">
                <Button variant="outline" size="sm" className="mt-1">Avise-me quando voltar</Button>
              </a>
            </div>
          )}

          <div className="bg-[#F7F5F2] rounded-lg p-4 space-y-2 text-sm">
            <div className="flex items-start gap-2"><Truck className="h-4 w-4 text-[#C91F1F] mt-0.5" /><span>Retirada grátis na loja Magé-RJ</span></div>
            <div className="flex items-start gap-2"><MapPin className="h-4 w-4 text-[#C91F1F] mt-0.5" /><span>Rua Dr. Siqueira, nº 269, Magé - RJ</span></div>
          </div>

          <Accordion type="single" collapsible defaultValue="desc">
            <AccordionItem value="desc"><AccordionTrigger>Descrição</AccordionTrigger><AccordionContent>{product.description}</AccordionContent></AccordionItem>
            <AccordionItem value="mat"><AccordionTrigger>Composição e material</AccordionTrigger><AccordionContent>{product.material || "-"}</AccordionContent></AccordionItem>
            <AccordionItem value="care"><AccordionTrigger>Cuidados</AccordionTrigger><AccordionContent>{product.care || "-"}</AccordionContent></AccordionItem>
          </Accordion>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-16">
          <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: "Outfit" }}>Produtos relacionados</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-6">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}
